import { parseAndFormatDate, parseAndFormatDateTime, toISO, deepEquals } from "@livefree/common";
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { Skeleton, Card, Link, ComboBox, Label, Input, ListBox, Alert, DatePicker, DateField, Calendar, DateRangePicker, RangeCalendar, TextField, InputGroup, CloseButton, Select, Slider, Switch, Popover, Chip, Dropdown, Button, Description, Tooltip, Separator, Drawer, ToggleButtonGroup, ToggleButton } from "@heroui/react";
import { getLocalTimeZone, parseDateTime, toZoned, toLocalTimeZone, toCalendarDateTime, parseDate } from "@internationalized/date";
class Filter {
  name;
  title;
  searchType;
  value;
  displayValue;
  values;
  scope;
  isEnabled;
  isVisible;
  isApplied;
  isLocked;
  render;
  watch;
  handlers;
  constructor(ctor) {
    this.name = ctor.name;
    this.title = ctor.title;
    this.searchType = ctor.searchType ?? "contains";
    this.value = ctor.value;
    this.values = ctor.values;
    this.scope = ctor.scope ?? "global";
    this.isEnabled = ctor.isEnabled ?? true;
    this.isVisible = ctor.isVisible ?? true;
    this.isApplied = ctor.isApplied ?? false;
    this.isLocked = ctor.isLocked ?? false;
    this.render = ctor.render;
    this.handlers = [];
    this.watch = ctor.watch;
    this.displayValue = ctor.displayValue ?? "";
  }
  subscribe(handler) {
    if (!this.handlers.includes(handler)) {
      this.handlers.push(handler);
    }
  }
  unsubscribe(handler) {
    if (this.handlers.includes(handler)) {
      this.handlers = this.handlers.slice(0, this.handlers.indexOf(handler)).concat(this.handlers.slice(this.handlers.indexOf(handler) + 1));
    }
  }
  setEnabled(enabled, resetValue) {
    this.isEnabled = enabled;
    if (!enabled) {
      this.value = resetValue;
      this.isApplied = false;
    }
    this.changed();
  }
  setVisible(visible, resetValue) {
    if (!this.isLocked) {
      this.isVisible = visible;
      if (!visible) {
        this.value = resetValue;
        this.isApplied = false;
      }
      this.changed();
    }
  }
  setApplied(applied, resetValue) {
    this.isApplied = applied;
    if (!applied) {
      this.value = resetValue;
    }
    this.changed();
  }
  setValue(value, applyImmediately = false) {
    this.value = this.convertValue(value);
    if (applyImmediately) {
      this.isApplied = value != null && value !== "";
    } else if (value == null || value === "") {
      this.isApplied = false;
    }
    this.changed();
  }
  setSearchType(type) {
    this.searchType = type;
    this.changed();
  }
  changed() {
    this.handlers.forEach((handler) => handler(this));
  }
  serializable() {
    return {
      name: this.name,
      title: this.title,
      searchType: this.searchType ?? "contains",
      value: this.value,
      displayValue: this.displayValue,
      scope: this.scope,
      isEnabled: this.isEnabled,
      isVisible: this.isVisible,
      isApplied: this.isApplied,
      isLocked: this.isLocked,
      render: this.render.serializable()
    };
  }
  convertValue(value) {
    switch (this.render.inputType) {
      case "boolean":
        return !!value;
      case "integer": {
        const parsed = parseInt(value?.toString() ?? "", 10);
        if (isNaN(parsed)) return 0;
        return parsed;
      }
      case "decimal": {
        const parsed = parseFloat(value?.toString() ?? "");
        if (isNaN(parsed)) return 0;
        return parsed;
      }
      default:
        return value;
    }
  }
}
class Render {
  filterType;
  inputType;
  isClearable;
  format;
  element;
  minValue;
  maxValue;
  stepValue;
  hideContainer;
  hideSearchType;
  hideApply;
  constructor(options) {
    this.filterType = options.filterType;
    this.inputType = options.inputType ?? "string";
    this.isClearable = options.isClearable ?? true;
    this.format = options.format;
    this.element = options.element;
    this.minValue = options.minValue ?? 0;
    this.maxValue = options.maxValue ?? 0;
    this.stepValue = options.stepValue ?? 0;
    this.hideContainer = options.hideContainer ?? false;
    this.hideSearchType = options.hideSearchType ?? false;
    this.hideApply = options.hideApply ?? false;
  }
  serializable() {
    return {
      filterType: this.filterType,
      inputType: this.inputType,
      isClearable: this.isClearable,
      format: this.format,
      minValue: this.minValue,
      maxValue: this.maxValue,
      stepValue: this.stepValue,
      hideContainer: this.hideContainer,
      hideSearchType: this.hideSearchType,
      hideApply: this.hideApply
    };
  }
}
class Values {
  load;
  constructor(load) {
    this.load = load;
  }
}
class FilterAutocomplete extends Filter {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      render: new Render({
        ...ctor.render ?? {},
        filterType: "autocomplete"
      }),
      ...ctor.options ?? {},
      value: ctor.defaultValue,
      values: new Values(ctor.loader)
    });
  }
  formattedValue() {
    const raw = this.value;
    if (raw == null || raw === "") {
      return "";
    }
    const opts = this.values?.options;
    if (opts && opts.length > 0) {
      const option = opts.find((x) => x.value === raw || String(x.value) === String(raw));
      if (option) {
        return option.label;
      }
    }
    return String(raw);
  }
}
class FilterConstant extends Filter {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      value: ctor.defaultValue,
      searchType: "equals",
      isApplied: true,
      isVisible: false,
      isEnabled: true,
      isLocked: true,
      scope: "custom",
      ...ctor.options ?? {},
      render: new Render({
        inputType: "string",
        ...ctor.render ?? {},
        filterType: "constant"
      })
    });
  }
  formattedValue() {
    if (this.value) {
      return this.value.toString();
    }
    return "";
  }
}
class FilterCustom extends Filter {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      render: new Render({
        ...ctor.render ?? {},
        filterType: "custom",
        element: ctor.customRender
      }),
      ...ctor.options ?? {},
      value: ctor.defaultValue
    });
  }
}
class FilterCustomSelect extends Filter {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      render: new Render({
        ...ctor.render ?? {},
        filterType: "customSelect",
        element: ctor.customRender
      }),
      ...ctor.options ?? {},
      value: ctor.defaultValue
    });
  }
}
class FilterDate extends Filter {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      render: new Render({
        ...ctor.render ?? {},
        filterType: "date"
      }),
      searchType: "equals",
      ...ctor.options ?? {},
      value: ctor.defaultValue
    });
  }
  formattedValue() {
    if (this.value) {
      return parseAndFormatDate(this.value);
    }
    return "";
  }
}
class FilterDateRange extends Filter {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      render: new Render({
        ...ctor.render ?? {},
        filterType: "dateRange"
      }),
      searchType: "between",
      ...ctor.options ?? {},
      value: ctor.defaultValue
    });
  }
  formattedValue() {
    if (Array.isArray(this.value)) {
      if (this.value[0] && this.value[1]) {
        return `${parseAndFormatDate(this.value[0])} - ${parseAndFormatDate(this.value[1])}`;
      }
    }
    return "";
  }
}
class FilterDateTime extends Filter {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      render: new Render({
        ...ctor.render ?? {},
        filterType: "dateTime"
      }),
      searchType: "equals",
      ...ctor.options ?? {},
      value: ctor.defaultValue
    });
  }
  formattedValue() {
    if (this.value) {
      return parseAndFormatDateTime(this.value);
    }
    return "";
  }
}
const FILTER_TYPES_WITHOUT_SEARCH_TYPE = [
  "toggle",
  "select",
  "multiSelect",
  "date",
  "dateTime",
  "dateRange",
  "range"
];
function isSearchTypeUserEditable(filter) {
  const { render } = filter;
  return !render.hideSearchType && !render.hideContainer && !FILTER_TYPES_WITHOUT_SEARCH_TYPE.includes(render.filterType);
}
class Filters {
  filters;
  handlers;
  constructor(filters) {
    this.filters = filters;
    this.handlers = [];
    this.filters.forEach((filter) => {
      filter.subscribe((f) => {
        this.handlers.forEach((handler) => handler(f));
      });
    });
    this.filters.filter((filter) => filter.watch).forEach((filter) => {
      filter.watch.filters.forEach((watched) => {
        const other = this.get(watched);
        if (other) {
          other.subscribe(filter.watch.handler.bind(null, filter, other));
        }
      });
    });
  }
  setValue(name, value, applyImmediately) {
    const filter = this.findFilter(name);
    if (filter) {
      filter.setValue(value, applyImmediately);
      this.handlers.forEach((handler) => handler(filter));
    }
  }
  setEnabled(name, enabled) {
    const filter = this.findFilter(name);
    if (filter) {
      filter.setEnabled(enabled);
      this.handlers.forEach((handler) => handler(filter));
    }
  }
  setSearchType(name, type) {
    const filter = this.findFilter(name);
    if (filter) {
      filter.setSearchType(type);
      this.handlers.forEach((handler) => handler(filter));
    }
  }
  //subscribe(handler: FilterChangedHandler): void;
  //subscribe(name: string, handler: FilterChangedHandler): void;
  subscribe(name, handler) {
    if (typeof name === "string") {
      const filter = this.findFilter(name);
      if (filter) {
        filter.subscribe(handler);
      }
    } else {
      handler = name;
      this.handlers.push(handler);
    }
  }
  //unsubscribe(handler: FilterChangedHandler): void;
  //unsubscribe(name: string, handler: FilterChangedHandler): void;
  unsubscribe(name, handler) {
    if (typeof name === "string") {
      const filter = this.findFilter(name);
      if (filter) {
        filter.unsubscribe(handler);
      }
    } else {
      handler = name;
      if (this.handlers.includes(handler)) {
        this.handlers = this.handlers.slice(0, this.handlers.indexOf(handler)).concat(this.handlers.slice(this.handlers.indexOf(handler) + 1));
      }
    }
  }
  get(name) {
    const filter = this.findFilter(name);
    return filter;
  }
  getAll(params) {
    return this.filters.filter((filter) => {
      switch (params.scope) {
        case "global":
          return filter.scope === "global";
        case "custom":
          return filter.scope === "custom";
        default:
          return true;
      }
    }).filter((filter) => {
      switch (params.visibility) {
        case "visible":
          return filter.isVisible;
        case "hidden":
          return !filter.isVisible;
        default:
          return true;
      }
    }).filter((filter) => {
      switch (params.status) {
        case "active":
          return filter.isApplied;
        case "inactive":
          return !filter.isApplied;
        default:
          return true;
      }
    });
  }
  getActive() {
    return this.getAll({
      scope: "all",
      visibility: "all",
      status: "active"
    });
  }
  clearAll(scope) {
    this.getAll({
      scope: scope ?? "all",
      visibility: "all",
      status: "active"
    }).forEach((filter) => filter.setApplied(false));
  }
  serialize() {
    const output = [];
    this.filters.forEach((filter) => {
      output.push(filter.serializable());
    });
    return JSON.stringify(output);
  }
  deserialize(serialized) {
    let obj;
    if (typeof serialized === "string") {
      obj = JSON.parse(serialized);
    } else {
      obj = serialized;
    }
    obj.forEach((x) => {
      const filter = this.get(x.name);
      if (filter && !filter.isLocked) {
        filter.value = x.value;
        filter.displayValue = x.displayValue ?? "";
        filter.isVisible = x.isVisible;
        filter.isApplied = x.isApplied;
        if (isSearchTypeUserEditable(filter)) {
          filter.searchType = x.searchType;
        }
      }
    });
    return this;
  }
  findFilter(name) {
    const filter = this.filters.find((filter2) => filter2.name.toLowerCase() === name.toLowerCase());
    return filter;
  }
}
class FilterInput extends Filter {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      render: new Render({
        ...ctor.render ?? {},
        filterType: "input"
      }),
      ...ctor.options ?? {},
      value: ctor.defaultValue
    });
  }
  formattedValue() {
    if (this.value) {
      return this.value.toString();
    }
    return "";
  }
}
class FilterMultiSelect extends Filter {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      render: new Render({
        ...ctor.render ?? {},
        filterType: "multiSelect"
      }),
      ...ctor.options ?? {},
      value: ctor.defaultValue,
      values: new Values(ctor.loader)
    });
  }
  formattedValue() {
    if (!this.value || !Array.isArray(this.value)) {
      return "";
    }
    const keys = this.value;
    const opts = this.values?.options;
    if (opts && opts.length > 0) {
      const selected = opts.filter((x) => keys.includes(x.value?.toString() ?? ""));
      if (selected.length > 0) {
        return selected.map((x) => x.label).join(", ");
      }
    }
    return keys.join(", ");
  }
}
class FilterNumericInput extends FilterInput {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      render: new Render({
        ...ctor.render ?? {},
        filterType: "input",
        inputType: ctor.numberType === "integer" ? "integer" : "decimal"
      }),
      searchType: "equals",
      ...ctor.options ?? {},
      defaultValue: ctor.defaultValue
    });
  }
}
const Operators = {
  ["equals"]: "==",
  ["notEquals"]: "!=",
  ["contains"]: "?",
  ["startsWith"]: "^",
  ["endsWith"]: "$",
  ["greaterThan"]: ">",
  ["greaterThanOrEquals"]: ">=",
  ["lessThan"]: "<",
  ["lessThanOrEquals"]: "<=",
  ["between"]: "@",
  ["in"]: ""
};
class FilterRange extends Filter {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      render: new Render({
        ...ctor.render ?? {},
        filterType: "range",
        minValue: ctor.minValue ?? 1,
        maxValue: ctor.maxValue ?? 100,
        stepValue: ctor.stepValue ?? 1,
        format: ctor.format ?? { style: "percent" }
      }),
      searchType: "between",
      ...ctor.options ?? {},
      value: ctor.defaultValue
    });
  }
  formattedValue() {
    if (Array.isArray(this.value)) {
      return `${this.value[0]} - ${this.value[1]}`;
    }
    return "";
  }
}
class FilterSelect extends Filter {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      render: new Render({
        ...ctor.render ?? {},
        filterType: "select"
      }),
      searchType: "equals",
      ...ctor.options ?? {},
      value: ctor.defaultValue,
      values: new Values(ctor.loader)
    });
  }
  formattedValue() {
    const raw = this.value;
    if (raw == null || raw === "") {
      return "";
    }
    const opts = this.values?.options;
    if (opts && opts.length > 0) {
      const option = opts.find((x) => x.value === raw || String(x.value) === String(raw));
      if (option) {
        return option.label;
      }
    }
    return String(raw);
  }
}
class FilterToggle extends Filter {
  constructor(ctor) {
    super({
      name: ctor.name,
      title: ctor.title,
      render: new Render({
        ...ctor.render ?? {},
        filterType: "toggle"
      }),
      searchType: "equals",
      ...ctor.options ?? {},
      value: ctor.defaultValue
    });
  }
  formattedValue() {
    return this.value ? "On" : "Off";
  }
}
const FiltersContext = createContext(null);
const useFiltersProvider = () => {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error(
      "FiltersContext not found. Did you forget to wrap your component with <FiltersProvider>?"
    );
  }
  return context;
};
function withFilterMonitor(Component) {
  const Wrapped = (props) => {
    const [value, setValue] = useState(props.filter.value);
    const { filters, triggerApply } = useFiltersProvider();
    useEffect(() => {
      const watcher = (f) => {
        if (f.value !== value) {
          setValue(f.value);
        }
      };
      props.filter.subscribe(watcher);
      return () => props.filter.unsubscribe(watcher);
    }, [props.filter, value]);
    const triggerUpdateHandler = useCallback(
      (val, applyImmediately = false) => {
        props.filter.setValue(val, applyImmediately);
        if (applyImmediately) {
          triggerApply();
        }
      },
      [props.filter, triggerApply]
    );
    const setValueHandler = useCallback(
      (value2, triggerUpdate = false, applyImmediately = false) => {
        setValue(value2);
        if (triggerUpdate) {
          triggerUpdateHandler(value2, applyImmediately);
        }
      },
      [triggerUpdateHandler]
    );
    return /* @__PURE__ */ jsx(
      Component,
      {
        ...props,
        filters,
        value,
        triggerUpdate: triggerUpdateHandler,
        setValue: setValueHandler
      }
    );
  };
  return Wrapped;
}
function withOptionsMonitor(Component) {
  const Wrapped = (props) => {
    const [items, setItems] = useState([]);
    const { filter, ...rest } = props;
    useEffect(() => {
      if (filter.values) {
        (async () => {
          const options = await filter.values.load(filter);
          filter.values.options = options;
          setItems(options);
        })();
      }
    }, [filter.values, filter]);
    if (items.length === 0) {
      return /* @__PURE__ */ jsx(Skeleton, { className: "h-10 w-full rounded-lg" });
    }
    return /* @__PURE__ */ jsx(Component, { filter, items, ...rest });
  };
  return Wrapped;
}
function withFilterContainer(Component, {
  includeStatus = true,
  includeApply = true,
  includeSearchType = false
} = {}) {
  const Wrapped = (props) => {
    const { filter, triggerUpdate, ...rest } = props;
    if (filter.render.hideContainer)
      return includeApply ? /* @__PURE__ */ jsxs("div", { className: "flex flex-row items-center gap-1", children: [
        /* @__PURE__ */ jsx(Component, { filter, triggerUpdate, ...rest }),
        /* @__PURE__ */ jsx(ApplyFiltersButton, { filter })
      ] }) : /* @__PURE__ */ jsx(Component, { filter, triggerUpdate, ...rest });
    return /* @__PURE__ */ jsxs(Card, { className: "w-full rounded-sm border border-accent/50 h-full", children: [
      /* @__PURE__ */ jsx(Card.Header, { className: "pt-2 pb-1", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-row grow items-center justify-center", children: [
        includeStatus && /* @__PURE__ */ jsx(StatusToggle, { filter }),
        /* @__PURE__ */ jsx("div", { className: "grow" }),
        includeSearchType && !filter.render.hideSearchType && /* @__PURE__ */ jsx("div", { className: "w-1/2 @sm:w-1/3 @lg:w-1/4 flex justify-end", children: /* @__PURE__ */ jsx(
          SearchTypeSelect,
          {
            searchType: filter.searchType,
            onChange: (type) => filter.setSearchType(type)
          }
        ) })
      ] }) }),
      /* @__PURE__ */ jsx(Card.Content, { className: "py-1 pb-2", children: /* @__PURE__ */ jsx("div", { className: "grow", children: /* @__PURE__ */ jsx(Component, { filter, triggerUpdate, ...rest }) }) }),
      includeApply && !filter.render.hideApply && /* @__PURE__ */ jsx(Card.Footer, { children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between grow", children: [
        /* @__PURE__ */ jsx(
          Link,
          {
            onPress: () => {
              filter.setApplied(false);
              triggerUpdate(void 0, true);
            },
            className: "cursor-pointer",
            isDisabled: !filter.isApplied,
            children: "Reset"
          }
        ),
        /* @__PURE__ */ jsx(ApplyFiltersButton, { filter })
      ] }) })
    ] });
  };
  return Wrapped;
}
function formatValue(type, value) {
  switch (type) {
    case "boolean":
      return !!value;
    case "string":
      return value?.toString() ?? "";
    case "decimal": {
      const parsedFloat = parseFloat(value?.toString() ?? "0");
      return isNaN(parsedFloat) ? 0 : parsedFloat;
    }
    case "integer": {
      const parsedInt = parseInt(value?.toString() ?? "0");
      return isNaN(parsedInt) ? 0 : parsedInt;
    }
    default:
      return value;
  }
}
const AutocompleteFilter = withFilterMonitor(
  withFilterContainer(
    withOptionsMonitor((props) => {
      const { filter, value, setValue, triggerUpdate, items, ...rest } = props;
      return /* @__PURE__ */ jsxs(
        ComboBox,
        {
          id: filter.name,
          name: filter.name,
          isDisabled: !filter.isEnabled,
          defaultItems: items,
          onSelectionChange: (k) => {
            setValue(formatValue(filter.render.inputType ?? "string", k), true, true);
          },
          selectedKey: value?.toString(),
          ...rest,
          children: [
            /* @__PURE__ */ jsx(Label, { children: filter.title }),
            /* @__PURE__ */ jsxs(ComboBox.InputGroup, { children: [
              /* @__PURE__ */ jsx(Input, { variant: "secondary" }),
              /* @__PURE__ */ jsx(ComboBox.Trigger, {})
            ] }),
            /* @__PURE__ */ jsx(ComboBox.Popover, { children: /* @__PURE__ */ jsx(ListBox, { children: (item) => /* @__PURE__ */ jsxs(ListBox.Item, { id: item.value?.toString() ?? "", textValue: item.label, children: [
              item.label,
              /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
            ] }) }) })
          ]
        }
      );
    }),
    { includeStatus: true, includeApply: false, includeSearchType: true }
  )
);
const CustomFilterError = (props) => /* @__PURE__ */ jsxs(Alert, { status: "danger", children: [
  /* @__PURE__ */ jsx(Alert.Indicator, {}),
  /* @__PURE__ */ jsx(Alert.Content, { children: /* @__PURE__ */ jsxs(Alert.Title, { children: [
    "[",
    props.filter.title,
    "(",
    props.filter.name,
    ")] Missing implementation"
  ] }) })
] });
const customFilter = (filter, { includeStatus = true, includeSearchType = false, includeApply = true }) => withFilterMonitor(
  withFilterContainer(filter.render.element ?? CustomFilterError, {
    includeStatus,
    includeSearchType,
    includeApply
  })
);
const customSelectFilter = (filter, { includeStatus = true, includeSearchType = false, includeApply = true }) => withFilterMonitor(
  withFilterContainer(withOptionsMonitor(filter.render.element ?? CustomFilterError), {
    includeStatus,
    includeSearchType,
    includeApply
  })
);
function convertDateIn(dt) {
  if (!dt) return void 0;
  return parseDate(dt.slice(0, 10));
}
function convertDateTimeIn(dt, zoned = false) {
  if (!dt) return void 0;
  const parsed = parseDateTime(dt);
  const utc = toZoned(parsed, "UTC");
  const local = toLocalTimeZone(utc);
  return zoned ? local : toCalendarDateTime(local);
}
function convertDateOut(dt, endOfDay = false) {
  if (dt) {
    const converted = dt.toDate(getLocalTimeZone());
    if (endOfDay) {
      converted.setMilliseconds(1e3 * 60 * 60 * 24 - 1);
    }
    return toISO(converted);
  }
}
const DateFilter = withFilterMonitor(
  withFilterContainer(
    (props) => {
      const { filter, value, setValue, triggerUpdate, includeTime, ...rest } = props;
      return /* @__PURE__ */ jsxs(
        DatePicker,
        {
          id: filter.name,
          name: filter.name,
          hideTimeZone: true,
          isDisabled: !filter.isEnabled,
          value: includeTime ?? false ? convertDateTimeIn(value ? value.toString() : null, true) : convertDateIn(value ? value.toString() : null),
          onChange: (dt) => setValue(convertDateOut(dt)),
          onBlur: () => triggerUpdate(value),
          ...rest,
          children: [
            /* @__PURE__ */ jsx(Label, { children: filter.title }),
            /* @__PURE__ */ jsxs(DateField.Group, { variant: "secondary", children: [
              /* @__PURE__ */ jsx(DateField.Input, { children: (segment) => /* @__PURE__ */ jsx(DateField.Segment, { segment }) }),
              /* @__PURE__ */ jsx(DateField.Suffix, { children: /* @__PURE__ */ jsx(DatePicker.Trigger, { children: /* @__PURE__ */ jsx(DatePicker.TriggerIndicator, {}) }) })
            ] }),
            /* @__PURE__ */ jsx(DatePicker.Popover, { children: /* @__PURE__ */ jsxs(Calendar, { "aria-label": filter.title, children: [
              /* @__PURE__ */ jsxs(Calendar.Header, { children: [
                /* @__PURE__ */ jsxs(Calendar.YearPickerTrigger, { children: [
                  /* @__PURE__ */ jsx(Calendar.YearPickerTriggerHeading, {}),
                  /* @__PURE__ */ jsx(Calendar.YearPickerTriggerIndicator, {})
                ] }),
                /* @__PURE__ */ jsx(Calendar.NavButton, { slot: "previous" }),
                /* @__PURE__ */ jsx(Calendar.NavButton, { slot: "next" })
              ] }),
              /* @__PURE__ */ jsxs(Calendar.Grid, { children: [
                /* @__PURE__ */ jsx(Calendar.GridHeader, { children: (day) => /* @__PURE__ */ jsx(Calendar.HeaderCell, { children: day }) }),
                /* @__PURE__ */ jsx(Calendar.GridBody, { children: (date) => /* @__PURE__ */ jsx(Calendar.Cell, { date }) })
              ] }),
              /* @__PURE__ */ jsx(Calendar.YearPickerGrid, { children: /* @__PURE__ */ jsx(Calendar.YearPickerGridBody, { children: (cell) => /* @__PURE__ */ jsx(Calendar.YearPickerCell, { year: cell.year }) }) })
            ] }) })
          ]
        }
      );
    },
    { includeStatus: true, includeApply: true, includeSearchType: false }
  )
);
const DateRangeFilter = withFilterMonitor(
  withFilterContainer(
    (props) => {
      const { filter, value, setValue, triggerUpdate, includeTime, ...rest } = props;
      const start = Array.isArray(value) ? value[0] : void 0;
      const end = Array.isArray(value) ? value[1] : void 0;
      return /* @__PURE__ */ jsxs(
        DateRangePicker,
        {
          id: filter.name,
          "aria-label": filter.title,
          hideTimeZone: true,
          isDisabled: !filter.isEnabled,
          value: {
            start: includeTime ?? false ? convertDateTimeIn(start ? start.toString() : null, true) : convertDateIn(start ? start.toString() : null),
            end: includeTime ?? false ? convertDateTimeIn(end ? end.toString() : null) : convertDateIn(end ? end.toString() : null)
          },
          onChange: (dt) => {
            if (dt) {
              setValue([convertDateOut(dt.start), convertDateOut(dt.end)], true);
            } else {
              setValue(void 0, true);
            }
          },
          onBlur: () => triggerUpdate(value),
          ...rest,
          children: [
            /* @__PURE__ */ jsx(Label, { children: filter.title }),
            /* @__PURE__ */ jsxs(DateField.Group, { variant: "secondary", children: [
              /* @__PURE__ */ jsxs(DateField.InputContainer, { children: [
                /* @__PURE__ */ jsx(DateField.Input, { slot: "start", children: (segment) => /* @__PURE__ */ jsx(DateField.Segment, { segment }) }),
                /* @__PURE__ */ jsx(DateRangePicker.RangeSeparator, {}),
                /* @__PURE__ */ jsx(DateField.Input, { slot: "end", children: (segment) => /* @__PURE__ */ jsx(DateField.Segment, { segment }) })
              ] }),
              /* @__PURE__ */ jsx(DateField.Suffix, { children: /* @__PURE__ */ jsx(DateRangePicker.Trigger, { children: /* @__PURE__ */ jsx(DateRangePicker.TriggerIndicator, {}) }) })
            ] }),
            /* @__PURE__ */ jsx(DateRangePicker.Popover, { children: /* @__PURE__ */ jsxs(RangeCalendar, { "aria-label": filter.title, children: [
              /* @__PURE__ */ jsxs(RangeCalendar.Header, { children: [
                /* @__PURE__ */ jsxs(RangeCalendar.YearPickerTrigger, { children: [
                  /* @__PURE__ */ jsx(RangeCalendar.YearPickerTriggerHeading, {}),
                  /* @__PURE__ */ jsx(RangeCalendar.YearPickerTriggerIndicator, {})
                ] }),
                /* @__PURE__ */ jsx(RangeCalendar.NavButton, { slot: "previous" }),
                /* @__PURE__ */ jsx(RangeCalendar.NavButton, { slot: "next" })
              ] }),
              /* @__PURE__ */ jsxs(RangeCalendar.Grid, { children: [
                /* @__PURE__ */ jsx(RangeCalendar.GridHeader, { children: (day) => /* @__PURE__ */ jsx(RangeCalendar.HeaderCell, { children: day }) }),
                /* @__PURE__ */ jsx(RangeCalendar.GridBody, { children: (date) => /* @__PURE__ */ jsx(RangeCalendar.Cell, { date }) })
              ] }),
              /* @__PURE__ */ jsx(RangeCalendar.YearPickerGrid, { children: /* @__PURE__ */ jsx(RangeCalendar.YearPickerGridBody, { children: (cell) => /* @__PURE__ */ jsx(RangeCalendar.YearPickerCell, { year: cell.year }) }) })
            ] }) })
          ]
        }
      );
    },
    { includeStatus: true, includeApply: true, includeSearchType: false }
  )
);
const InputFilter = withFilterMonitor(
  withFilterContainer(
    (props) => {
      const { filter, value, setValue, triggerUpdate, ...rest } = props;
      return /* @__PURE__ */ jsx(
        TextField,
        {
          id: filter.name,
          name: filter.name,
          "aria-label": filter.title || filter.name,
          value: value?.toString() ?? "",
          onChange: (val) => setValue(val),
          onBlur: () => triggerUpdate(value),
          isDisabled: !filter.isEnabled,
          fullWidth: true,
          ...rest,
          children: /* @__PURE__ */ jsxs(InputGroup, { variant: "secondary", children: [
            /* @__PURE__ */ jsx(
              InputGroup.Input,
              {
                placeholder: filter.title,
                autoComplete: "off",
                onKeyUp: (evt) => {
                  if (evt.key.toLowerCase() === "enter") {
                    evt.preventDefault();
                    triggerUpdate(value, true);
                  }
                }
              }
            ),
            !!value && /* @__PURE__ */ jsx(InputGroup.Suffix, { children: /* @__PURE__ */ jsx(
              CloseButton,
              {
                "aria-label": "Clear filter",
                onPress: () => {
                  filter.setApplied(false);
                  triggerUpdate(void 0, true);
                }
              }
            ) })
          ] })
        }
      );
    },
    { includeStatus: true, includeApply: true, includeSearchType: true }
  )
);
const MultiSelectFilter = withFilterMonitor(
  withFilterContainer(
    withOptionsMonitor((props) => {
      const { filter, value, setValue, items, triggerUpdate, ...rest } = props;
      return /* @__PURE__ */ jsxs(
        Select,
        {
          id: filter.name,
          name: filter.name,
          isDisabled: !filter.isEnabled,
          selectionMode: "multiple",
          variant: "secondary",
          onChange: (k) => {
            const keys = (Array.isArray(k) ? k : [k]).map(
              (x) => formatValue(filter.render.inputType ?? "string", x)
            );
            setValue(keys);
          },
          value: (
            // If there aren't any items, but there are selected values
            // set selected to an empty array to suppress console warning
            (items?.length ?? 0) === 0 ? [] : Array.isArray(value) ? value.map((x) => x.toString()) : [value?.toString() ?? ""]
          ),
          ...rest,
          onBlur: () => triggerUpdate(value),
          children: [
            /* @__PURE__ */ jsx(Label, { children: filter.title }),
            /* @__PURE__ */ jsxs(Select.Trigger, { children: [
              /* @__PURE__ */ jsx(Select.Value, {}),
              /* @__PURE__ */ jsx(Select.Indicator, {})
            ] }),
            /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items, children: (item) => /* @__PURE__ */ jsxs(ListBox.Item, { id: item.value?.toString() ?? "", textValue: item.label, children: [
              item.label,
              /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
            ] }) }) })
          ]
        }
      );
    }),
    { includeStatus: true, includeApply: true, includeSearchType: false }
  )
);
function convertNumber(val, defaultMin = 0, defaultMax = 0) {
  if (val == null) return [defaultMin, defaultMax];
  if (Array.isArray(val)) {
    return [safeParse(val[0], defaultMin), safeParse(val[1], defaultMax)];
  }
  return [defaultMin, safeParse(val, defaultMax)];
}
function safeParse(val, defaultValue = 0) {
  const parsed = parseInt(val?.toString() ?? "", 10);
  return isNaN(parsed) ? defaultValue : parsed;
}
const RangeFilter = withFilterMonitor(
  withFilterContainer(
    (props) => {
      const { filter, value, setValue, triggerUpdate, ...rest } = props;
      return /* @__PURE__ */ jsxs(
        Slider,
        {
          id: filter.name,
          defaultValue: convertNumber(
            value,
            filter.render.minValue ?? 0,
            filter.render.maxValue ?? 100
          ),
          minValue: filter.render.minValue ?? 0,
          maxValue: filter.render.maxValue ?? 0,
          step: filter.render.stepValue ?? 0,
          formatOptions: {
            style: filter.render.format?.style ?? "decimal",
            currency: filter.render.format?.currency,
            numberingSystem: filter.render.format?.numberingSystem,
            minimumIntegerDigits: filter.render.format?.minimumIntegerDigits,
            minimumSignificantDigits: filter.render.format?.minimumSignificantDigits,
            maximumSignificantDigits: filter.render.format?.maximumSignificantDigits
          },
          onChangeEnd: (val) => setValue(val, true, true),
          isDisabled: !filter.isEnabled,
          ...rest,
          children: [
            /* @__PURE__ */ jsx(Label, { children: filter.title }),
            /* @__PURE__ */ jsx(Slider.Output, {}),
            /* @__PURE__ */ jsxs(Slider.Track, { children: [
              /* @__PURE__ */ jsx(Slider.Fill, {}),
              /* @__PURE__ */ jsx(Slider.Thumb, {})
            ] })
          ]
        }
      );
    },
    { includeStatus: true, includeApply: false, includeSearchType: false }
  )
);
const SelectFilter = withFilterMonitor(
  withFilterContainer(
    withOptionsMonitor((props) => {
      const { filter, value, setValue, triggerUpdate, items, ...rest } = props;
      return /* @__PURE__ */ jsxs(
        Select,
        {
          id: filter.name,
          name: filter.name,
          isDisabled: !filter.isEnabled,
          onChange: (k) => {
            if (k != null) {
              setValue(formatValue(filter.render.inputType ?? "string", k), true, true);
            }
          },
          value: (items?.length ?? 0) > 0 ? value?.toString() ?? null : null,
          variant: "secondary",
          ...rest,
          children: [
            /* @__PURE__ */ jsx(Label, { children: filter.title }),
            /* @__PURE__ */ jsxs(Select.Trigger, { children: [
              /* @__PURE__ */ jsx(Select.Value, {}),
              /* @__PURE__ */ jsx(Select.Indicator, {})
            ] }),
            /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items, children: (item) => /* @__PURE__ */ jsxs(ListBox.Item, { id: item.value?.toString() ?? "", textValue: item.label, children: [
              item.label,
              /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
            ] }) }) })
          ]
        }
      );
    }),
    { includeStatus: true, includeApply: false, includeSearchType: false }
  )
);
const ToggleFilter = withFilterMonitor(
  withFilterContainer(
    (props) => {
      const { filter, value, setValue, triggerUpdate, ...rest } = props;
      return /* @__PURE__ */ jsx(
        Switch,
        {
          id: filter.name,
          name: filter.name,
          isSelected: !!value,
          isDisabled: !filter.isEnabled,
          onChange: (selected) => {
            setValue(selected, true, true);
          },
          ...rest,
          children: /* @__PURE__ */ jsxs(Switch.Content, { children: [
            /* @__PURE__ */ jsx(Switch.Control, { children: /* @__PURE__ */ jsx(Switch.Thumb, {}) }),
            /* @__PURE__ */ jsx(Label, { children: filter.title })
          ] })
        }
      );
    },
    { includeStatus: true, includeApply: false, includeSearchType: false }
  )
);
const CUSTOM_OPTIONS = { includeStatus: true, includeSearchType: true };
function RenderFilter({ filter }) {
  const { filterType } = filter.render;
  const CustomFilter = useMemo(() => {
    if (filterType === "custom") return customFilter(filter, CUSTOM_OPTIONS);
    if (filterType === "customSelect") return customSelectFilter(filter, CUSTOM_OPTIONS);
    return null;
  }, [filter, filterType]);
  switch (filterType) {
    case "input":
      return /* @__PURE__ */ jsx(InputFilter, { filter });
    case "toggle":
      return /* @__PURE__ */ jsx(ToggleFilter, { filter });
    case "select":
      return /* @__PURE__ */ jsx(SelectFilter, { filter });
    case "multiSelect":
      return /* @__PURE__ */ jsx(MultiSelectFilter, { filter });
    case "date":
      return /* @__PURE__ */ jsx(DateFilter, { filter, includeTime: false });
    case "dateTime":
      return /* @__PURE__ */ jsx(DateFilter, { filter, includeTime: true });
    case "dateRange":
      return /* @__PURE__ */ jsx(DateRangeFilter, { filter });
    case "autocomplete":
      return /* @__PURE__ */ jsx(AutocompleteFilter, { filter });
    case "range":
      return /* @__PURE__ */ jsx(RangeFilter, { filter });
    case "custom":
    case "customSelect":
      return CustomFilter ? /* @__PURE__ */ jsx(CustomFilter, { filter }) : /* @__PURE__ */ jsx(InputFilter, { filter });
    default:
      return /* @__PURE__ */ jsx(InputFilter, { filter });
  }
}
function AppliedFiltersChipGenerator({
  scope = "global",
  color = "accent",
  variant = "secondary",
  size = "sm",
  endContent,
  children
}) {
  const { filters, triggerApply } = useFiltersProvider();
  const render = useCallback(
    (filter) => {
      return /* @__PURE__ */ jsxs(Popover, { children: [
        /* @__PURE__ */ jsx(Popover.Trigger, { children: /* @__PURE__ */ jsxs(Chip, { className: "cursor-pointer", size, color, variant, children: [
          /* @__PURE__ */ jsx("i", { className: "ri-filter-line" }),
          /* @__PURE__ */ jsxs("span", { className: "relative top-px", children: [
            filter.title,
            ": ",
            filter.formattedValue()
          ] }),
          /* @__PURE__ */ jsx(
            CloseButton,
            {
              "aria-label": "Remove filter",
              onPress: () => {
                filter.setApplied(false);
                triggerApply();
              }
            }
          )
        ] }) }),
        /* @__PURE__ */ jsx(Popover.Content, { className: "w-96", children: /* @__PURE__ */ jsxs(Popover.Dialog, { children: [
          /* @__PURE__ */ jsx(Popover.Arrow, {}),
          /* @__PURE__ */ jsx(RenderFilter, { filter })
        ] }) })
      ] });
    },
    [color, variant, size, triggerApply]
  );
  const applied = filters.getAll({
    scope,
    visibility: "all",
    status: "active"
  });
  if (applied.length > 0) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      applied.map((filter) => children(filter, render)),
      endContent && endContent
    ] });
  }
  return null;
}
function AppliedFiltersDropdown() {
  const { filters } = useFiltersProvider();
  const selected = filters.getAll({
    scope: "all",
    visibility: "all",
    status: "active"
  });
  return /* @__PURE__ */ jsxs(Dropdown, { children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        "aria-label": "Applied filters",
        size: "sm",
        isIconOnly: true,
        className: "rounded-full",
        variant: "primary",
        isDisabled: selected.length === 0,
        children: /* @__PURE__ */ jsx("i", { className: "ri-filter-3-line" })
      }
    ),
    /* @__PURE__ */ jsx(Dropdown.Popover, { children: /* @__PURE__ */ jsxs(Dropdown.Menu, { "aria-label": "Applied filters", children: [
      /* @__PURE__ */ jsx(Dropdown.Section, { children: selected.map((filter) => /* @__PURE__ */ jsxs(Dropdown.Item, { id: filter.name, textValue: filter.title, children: [
        /* @__PURE__ */ jsx("i", { className: "ri-filter-line" }),
        /* @__PURE__ */ jsx(Label, { children: filter.title }),
        /* @__PURE__ */ jsx(Description, { children: filter.formattedValue() }),
        /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: "danger-soft",
              className: "rounded-full",
              isIconOnly: true,
              onPress: () => filter.setApplied(false),
              children: /* @__PURE__ */ jsx("i", { className: "ri-close-line" })
            }
          ),
          /* @__PURE__ */ jsx(Tooltip.Content, { children: "Clear filter" })
        ] })
      ] }, filter.name)) }),
      /* @__PURE__ */ jsx(Separator, {}),
      /* @__PURE__ */ jsx(Dropdown.Section, { children: /* @__PURE__ */ jsx(
        Dropdown.Item,
        {
          id: "clearAll",
          textValue: "Clear All",
          variant: "danger",
          onPress: () => filters.clearAll(),
          className: "text-center",
          children: /* @__PURE__ */ jsx(Label, { children: "Clear All" })
        }
      ) })
    ] }) })
  ] });
}
function ApplyFiltersButton({ filter }) {
  const { triggerApply } = useFiltersProvider();
  return /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        size: "sm",
        variant: "primary",
        className: "rounded-full",
        isIconOnly: true,
        "aria-label": "Apply filter",
        onPress: () => {
          filter.setApplied(true);
          triggerApply();
        },
        children: /* @__PURE__ */ jsx("i", { className: "ri-check-line" })
      }
    ),
    /* @__PURE__ */ jsx(Tooltip.Content, { children: "Apply filter" })
  ] });
}
function AvailableFiltersSelect() {
  const { filters } = useFiltersProvider();
  const handleSelect = (name) => {
    const filter = filters.get(name);
    if (filter) {
      filter.setVisible(true);
    }
  };
  const items = filters.getAll({
    scope: "global",
    visibility: "hidden"
  });
  return /* @__PURE__ */ jsxs(
    Select,
    {
      isDisabled: items.length === 0,
      value: null,
      onChange: (key) => key != null && handleSelect(key.toString()),
      children: [
        /* @__PURE__ */ jsx(Label, { children: "Add Filter" }),
        /* @__PURE__ */ jsxs(Select.Trigger, { className: "rounded-full", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-filter-line" }),
          /* @__PURE__ */ jsx(Select.Value, {}),
          /* @__PURE__ */ jsx(Select.Indicator, {})
        ] }),
        /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items, children: (filter) => /* @__PURE__ */ jsxs(ListBox.Item, { id: filter.name, textValue: filter.title, children: [
          filter.title,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }) }) })
      ]
    }
  );
}
function ClearFiltersButton({ scope }) {
  const { filters, triggerApply } = useFiltersProvider();
  const active = filters.getAll({
    scope,
    visibility: "all",
    status: "active"
  }).length > 0;
  return active ? /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        size: "sm",
        variant: "danger-soft",
        className: "rounded-full",
        isIconOnly: true,
        onPress: () => {
          filters.clearAll(scope);
          triggerApply();
        },
        children: /* @__PURE__ */ jsx("i", { className: "ri-delete-bin-line" })
      }
    ),
    /* @__PURE__ */ jsx(Tooltip.Content, { children: "Clear all filters" })
  ] }) : null;
}
const RenderFilters = ({
  children,
  scope,
  visibility,
  status
}) => {
  const { filters } = useFiltersProvider();
  const render = useCallback((filter) => /* @__PURE__ */ jsx(RenderFilter, { filter }), []);
  const list = filters.getAll({
    scope,
    visibility,
    status
  });
  return /* @__PURE__ */ jsx(Fragment, { children: list.map((filter) => children(filter, render)) });
};
const HorizontalFilters = ({
  scope = "global",
  classNames
}) => {
  return /* @__PURE__ */ jsx("div", { className: `flex flex-col sm:flex-row items-center gap-2 ${classNames?.base ?? ""}`, children: /* @__PURE__ */ jsx(RenderFilters, { scope, visibility: "visible", children: (filter, render) => /* @__PURE__ */ jsx("div", { className: `w-full sm:w-96 ${classNames?.filterWrapper ?? ""}`, children: render(filter) }, filter.name) }) });
};
const VerticalFilters = () => {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsx(RenderFilters, { scope: "global", visibility: "visible", children: (filter, render) => /* @__PURE__ */ jsx("div", { children: render(filter) }, filter.name) }),
    /* @__PURE__ */ jsx("div", { className: "mt-4", children: /* @__PURE__ */ jsx(AvailableFiltersSelect, {}) })
  ] });
};
function FilterDrawer({
  isOpen,
  onClose,
  placement = "right",
  ...rest
}) {
  const isHorizontal = placement === "top" || placement === "bottom";
  const isVertical = placement === "left" || placement === "right";
  return /* @__PURE__ */ jsx(
    Drawer.Backdrop,
    {
      variant: "transparent",
      isOpen,
      onOpenChange: (open) => !open && onClose(),
      ...rest,
      children: /* @__PURE__ */ jsx(Drawer.Content, { placement, children: /* @__PURE__ */ jsx(Drawer.Dialog, { "aria-label": "Filters", className: isHorizontal ? "max-h-128" : "w-sm", children: /* @__PURE__ */ jsxs(Drawer.Body, { className: "px-2", children: [
        isVertical && /* @__PURE__ */ jsx(VerticalFilters, {}),
        isHorizontal && /* @__PURE__ */ jsx(HorizontalFilters, {})
      ] }) }) })
    }
  );
}
function FilterDrawerToggle({
  placement,
  ...rest
}) {
  const [isOpen, setIsOpen] = useState(false);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(Button, { size: "sm", variant: "primary", ...rest, onPress: () => setIsOpen(true), children: [
      /* @__PURE__ */ jsx("i", { className: isOpen ? "ri-filter-off-line" : "ri-filter-line" }),
      /* @__PURE__ */ jsx("span", { className: "relative top-px", children: "Filter" })
    ] }),
    /* @__PURE__ */ jsx(FilterDrawer, { placement, isOpen, onClose: () => setIsOpen(false) })
  ] });
}
function applyFilterPreset(filters, preset, clearExisting = true) {
  if (clearExisting) {
    const namesInPreset = new Set(preset.values.map((v) => v.filterName.toLowerCase()));
    for (const filter of filters.filters) {
      if (!namesInPreset.has(filter.name.toLowerCase())) {
        filter.setApplied(false);
      }
    }
  }
  for (const { filterName, value } of preset.values) {
    const filter = filters.get(filterName);
    if (filter) {
      filter.setValue(value, true);
    }
  }
}
function isFilterPresetActive(filters, preset) {
  return preset.values.length > 0 && preset.values.every(({ filterName, value }) => {
    const filter = filters.get(filterName);
    return !!filter && filter.isApplied && deepEquals(filter.value, value);
  });
}
function FilterPresetChips({
  presets,
  "aria-label": ariaLabel = "Presets",
  className,
  size = "md",
  isDisabled = false,
  clearExisting = true
}) {
  const { filters, triggerApply } = useFiltersProvider();
  const selectedKeys = new Set(
    presets.flatMap(
      (preset, index) => isFilterPresetActive(filters, preset) ? [String(index)] : []
    )
  );
  const onSelectionChange = useCallback(
    (keys) => {
      if (keys === "all") return;
      const [key] = keys;
      if (key == null) return;
      const preset = presets[Number(key)];
      if (!preset) return;
      applyFilterPreset(filters, preset, clearExisting);
      triggerApply();
    },
    [presets, filters, clearExisting, triggerApply]
  );
  if (presets.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    ToggleButtonGroup,
    {
      "aria-label": ariaLabel,
      className,
      size,
      isDetached: true,
      isDisabled,
      selectionMode: "single",
      disallowEmptySelection: true,
      selectedKeys,
      onSelectionChange,
      children: presets.map((preset, index) => /* @__PURE__ */ jsxs(
        ToggleButton,
        {
          id: String(index),
          className: "h-auto min-w-32 flex-col items-start gap-0.5 px-3.5 py-2 text-left",
          children: [
            /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold leading-tight", children: preset.name }),
            preset.description && /* @__PURE__ */ jsx("span", { className: "text-xs font-normal leading-tight opacity-70", children: preset.description })
          ]
        },
        preset.name
      ))
    }
  );
}
function FilterPresets({
  presets,
  label = "Presets",
  placeholder = "Search or choose a preset…",
  className,
  isDisabled = false,
  clearExisting = true
}) {
  const { filters, triggerApply } = useFiltersProvider();
  const [selectedKey, setSelectedKey] = useState(null);
  const items = useMemo(
    () => presets.map((preset, index) => ({
      key: String(index),
      label: preset.name,
      preset
    })),
    [presets]
  );
  const applyPreset = useCallback(
    (preset) => {
      applyFilterPreset(filters, preset, clearExisting);
      triggerApply();
    },
    [clearExisting, filters, triggerApply]
  );
  const onSelectionChange = useCallback(
    (key) => {
      setSelectedKey(key == null ? null : String(key));
      if (key == null) {
        return;
      }
      const item = items.find((i) => i.key === String(key));
      if (!item) {
        return;
      }
      applyPreset(item.preset);
      setSelectedKey(null);
    },
    [applyPreset, items]
  );
  if (presets.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs(
    ComboBox,
    {
      className,
      isDisabled,
      allowsCustomValue: false,
      selectedKey,
      onSelectionChange,
      defaultItems: items,
      "aria-label": label,
      children: [
        /* @__PURE__ */ jsxs(ComboBox.InputGroup, { children: [
          /* @__PURE__ */ jsx(Input, { variant: "secondary", placeholder }),
          /* @__PURE__ */ jsx(ComboBox.Trigger, {})
        ] }),
        /* @__PURE__ */ jsx(ComboBox.Popover, { children: /* @__PURE__ */ jsx(ListBox, { children: (item) => /* @__PURE__ */ jsxs(ListBox.Item, { id: item.key, textValue: item.label, children: [
          item.label,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }) }) })
      ]
    }
  );
}
function HorizontalChips({
  color,
  variant,
  scope
}) {
  return /* @__PURE__ */ jsx("div", { className: "flex flex-row items-center gap-2", children: /* @__PURE__ */ jsx(
    AppliedFiltersChipGenerator,
    {
      color,
      variant,
      scope,
      endContent: /* @__PURE__ */ jsx(ClearFiltersButton, { scope }),
      children: (filter, render) => /* @__PURE__ */ jsx("div", { children: render(filter) }, filter.name)
    }
  ) });
}
function SearchTypeSelect({
  searchType,
  onChange
}) {
  const items = useMemo(
    () => [
      { value: "equals", label: "=" },
      { value: "notEquals", label: "<>" },
      { value: "contains", label: "Contains" },
      { value: "startsWith", label: "Starts With" },
      { value: "endsWith", label: "Ends With" },
      { value: "greaterThan", label: ">" },
      { value: "greaterThanOrEquals", label: "> or =" },
      { value: "lessThan", label: "<" },
      { value: "lessThanOrEquals", label: "< or =" },
      { value: "between", label: "Between" },
      { value: "in", label: "In" }
    ],
    []
  );
  return /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
    /* @__PURE__ */ jsx(Tooltip.Trigger, { children: /* @__PURE__ */ jsxs(
      Select,
      {
        "aria-label": "searchType",
        value: searchType.toString(),
        variant: "secondary",
        onChange: (key) => {
          if (key != null) {
            onChange(key.toString());
          }
        },
        children: [
          /* @__PURE__ */ jsxs(Select.Trigger, { children: [
            /* @__PURE__ */ jsx(Select.Value, {}),
            /* @__PURE__ */ jsx(Select.Indicator, {})
          ] }),
          /* @__PURE__ */ jsx(Select.Popover, { children: /* @__PURE__ */ jsx(ListBox, { items, children: (item) => /* @__PURE__ */ jsxs(ListBox.Item, { id: item.value, textValue: item.label, children: [
            item.label,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ] }) }) })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(Tooltip.Content, { placement: "left", children: "Search type" })
  ] });
}
function StatusToggle({ filter }) {
  if (filter.isLocked) {
    return /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
      /* @__PURE__ */ jsx(Tooltip.Trigger, { children: /* @__PURE__ */ jsx("i", { className: "ri-git-repository-private-line text-accent text-sm px-2" }) }),
      /* @__PURE__ */ jsx(Tooltip.Content, { children: "This filter cannot be modified" })
    ] });
  }
  return /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        variant: "tertiary",
        size: "sm",
        className: "rounded-full",
        isIconOnly: true,
        onPress: () => filter.setVisible(false),
        children: /* @__PURE__ */ jsx("i", { className: "ri-eye-off-line" })
      }
    ),
    /* @__PURE__ */ jsx(Tooltip.Content, { children: "Hide filter" })
  ] });
}
function VerticalChips({
  color,
  variant,
  scope
}) {
  return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", children: /* @__PURE__ */ jsx(
    AppliedFiltersChipGenerator,
    {
      color,
      variant,
      scope,
      endContent: /* @__PURE__ */ jsx("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx(ClearFiltersButton, {}) }),
      children: (filter, render) => /* @__PURE__ */ jsx("div", { children: render(filter) }, filter.name)
    }
  ) });
}
function FiltersProvider({ filters, onApply, children }) {
  const [updatedOn, setUpdatedOn] = useState(Date());
  useEffect(() => {
    const handler = () => {
      setUpdatedOn(Date());
    };
    filters.subscribe(handler);
    return () => filters.unsubscribe(handler);
  }, [filters]);
  const context = useMemo(
    () => ({
      filters,
      triggerApply: () => onApply && onApply(filters),
      updatedOn
    }),
    [filters, updatedOn, onApply]
  );
  return /* @__PURE__ */ jsx(FiltersContext.Provider, { value: context, children });
}
export {
  AppliedFiltersChipGenerator,
  AppliedFiltersDropdown,
  ApplyFiltersButton,
  AutocompleteFilter,
  AvailableFiltersSelect,
  ClearFiltersButton,
  DateFilter,
  DateRangeFilter,
  Filter,
  FilterAutocomplete,
  FilterConstant,
  FilterCustom,
  FilterCustomSelect,
  FilterDate,
  FilterDateRange,
  FilterDateTime,
  FilterDrawer,
  FilterDrawerToggle,
  FilterInput,
  FilterMultiSelect,
  FilterNumericInput,
  FilterPresetChips,
  FilterPresets,
  FilterRange,
  FilterSelect,
  FilterToggle,
  Filters,
  FiltersProvider,
  HorizontalChips,
  HorizontalFilters,
  InputFilter,
  MultiSelectFilter,
  Operators,
  RangeFilter,
  Render,
  RenderFilter,
  RenderFilters,
  SearchTypeSelect,
  SelectFilter,
  StatusToggle,
  ToggleFilter,
  Values,
  VerticalChips,
  VerticalFilters,
  applyFilterPreset,
  customFilter,
  customSelectFilter,
  isFilterPresetActive,
  useFiltersProvider,
  withFilterContainer,
  withFilterMonitor,
  withOptionsMonitor
};
//# sourceMappingURL=index.js.map
